import React, { useState } from 'react';
import { Calculator, TrendingUp, AlertCircle, Building2, DollarSign, Percent } from 'lucide-react';

const ViabilityCalculator = () => {
  const [propertyPrice, setPropertyPrice] = useState<number>(94815.14);
  const [showTrial, setShowTrial] = useState(false);
  
  const calculateCosts = (price: number) => {
    const auctioneerFee = price * 0.05;
    const propertyTax = price * 0.07;
    const painting = 2000;
    const condoAndTaxes = 6000;
    const keymaker = 100;
    
    const totalInvestment = price + auctioneerFee + propertyTax + painting + condoAndTaxes + keymaker;
    const expectedSale = 170000;
    const brokerFee = expectedSale * 0.06;
    const netProfit = expectedSale - totalInvestment - brokerFee;
    const roi = (netProfit / totalInvestment) * 100;
    
    return {
      auctioneerFee,
      propertyTax,
      painting,
      condoAndTaxes,
      keymaker,
      totalInvestment,
      expectedSale,
      brokerFee,
      netProfit,
      roi
    };
  };

  const costs = calculateCosts(propertyPrice);

  return (
    <section className="py-20 bg-white" id="calculadora">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900">
            Calculadora de Viabilidade
          </h2>
          <p className="text-xl text-slate-600">
            Experimente nossa calculadora e veja como analisamos todos os custos do seu investimento
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden">
            <div className="p-6 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center gap-2 text-blue-700">
                <Calculator className="h-5 w-5" />
                <h3 className="font-semibold">Simulador de Custos e Retorno</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Input Section */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Valor do Imóvel em Leilão
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input
                        type="number"
                        value={propertyPrice}
                        onChange={(e) => setPropertyPrice(Number(e.target.value))}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Valor de Mercado Estimado
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <input
                        type="number"
                        value={170000}
                        disabled
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Desconto sobre o Valor de Mercado
                    </label>
                    <div className="relative">
                      <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
                      <input
                        type="text"
                        value="44.2%"
                        disabled
                        className="w-full pl-10 pr-4 py-2 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium"
                      />
                    </div>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-yellow-700 flex-shrink-0 mt-0.5" />
                      <div>
                        <h5 className="font-medium text-yellow-700">Versão de Demonstração</h5>
                        <p className="text-sm text-yellow-600 mt-1">
                          Na versão completa você tem acesso a:
                        </p>
                        <ul className="text-sm text-yellow-600 mt-2 space-y-1">
                          <li>• Análise detalhada de custos de reforma</li>
                          <li>• Comparativo com imóveis similares</li>
                          <li>• Projeção de valorização por região</li>
                          <li>• Simulação de financiamento</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Section */}
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-lg text-slate-900 mb-4">Custos de Aquisição</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Taxa do Leiloeiro (5%)</span>
                        <span className="font-medium">
                          R$ {costs.auctioneerFee.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">ITBI (7%)</span>
                        <span className="font-medium">
                          R$ {costs.propertyTax.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Custos Iniciais</span>
                        <span className="font-medium">
                          R$ {(costs.painting + costs.condoAndTaxes + costs.keymaker).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 bg-blue-50 px-3 rounded-lg">
                        <span className="font-semibold text-blue-900">Investimento Total</span>
                        <span className="font-semibold text-blue-900">
                          R$ {costs.totalInvestment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-lg text-slate-900 mb-4">Análise de Retorno</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Venda Esperada</span>
                        <span className="font-medium">
                          R$ {costs.expectedSale.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-slate-600">Comissão (6%)</span>
                        <span className="font-medium">
                          R$ {costs.brokerFee.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 bg-green-50 px-3 rounded-lg">
                        <span className="font-semibold text-green-900">Lucro Estimado</span>
                        <span className="font-semibold text-green-900">
                          R$ {costs.netProfit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between py-2 bg-blue-50 px-3 rounded-lg">
                        <span className="font-semibold text-blue-900">ROI</span>
                        <span className="font-semibold text-blue-900">
                          {costs.roi.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <a
              href="https://pay.kiwify.com.br/cSvs3ux"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <TrendingUp className="h-5 w-5" />
              Acessar Calculadora Completa
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ViabilityCalculator;